/**
 * One store, one seed, one clock.
 *
 * Persisted to localStorage under sv-cloud-demo so a refresh mid demo does not
 * reset the room. The demo dock's Reset clears the key and reseeds.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DemoData, Prefs, ScenarioId, Timeline } from '../types';
import { seed } from './seed';

export const STORAGE_KEY = 'sv-cloud-demo';

/**
 * The appearance a person gets before they have touched Appearance at all.
 *
 * It is an explicit 'day', not 'system'. The two are nowhere near the same
 * thing. 'system' is a statement that the operating system decides, so on a
 * machine set to dark it answers Night, and a first time visitor arriving from
 * a dark laptop would never see the launch look at all. What was asked for is a
 * site that opens in Day for someone who has expressed nothing, so the default
 * belongs on the choice axis, where a person can see it selected in the picker
 * and move it, rather than inside the rule that resolves 'system'. Teaching
 * that rule to answer Day against a dark operating system would leave "Sync
 * with system" advertising a sync it does not perform, which is the same
 * dishonesty the Cleanroom clamp exists to avoid.
 *
 * Nothing here overrides a person who has already chosen. The persist
 * middleware restores a stored prefs object whole, so Night and Sync with
 * system both survive a reload untouched. This value is read only when there is
 * nothing stored, and its plain string copy in localStorage is what the
 * bootstrap in index.html falls back to for the same reason. The two have to
 * name the same theme or the first frame and the bundle disagree.
 *
 * THE INVARIANT, and the reason theme below moved to 'day' with it: the default
 * theme has to be the theme resolveTheme() returns for the default choice under
 * BOTH operating system settings. 'system' could never satisfy that, because
 * its answer depends on the machine, and that mismatch is what let "Reset the
 * demo" hand the controller a theme its own rule disagreed with. The promotion
 * guard in lib/hooks/use-theme.ts records what the mismatch cost.
 */
export const DEFAULT_THEME_CHOICE: NonNullable<Prefs['themeChoice']> = 'day';

export const DEFAULT_PREFS: Prefs = {
  direction: 'instrument',
  // The resolved value for DEFAULT_THEME_CHOICE under the default direction,
  // which is what the invariant above requires. The controller in
  // lib/hooks/use-theme.ts is still the only thing that writes it after boot.
  theme: 'day',
  lang: 'en',
  speed: 4,
  advanced: false,
  nextBuildFails: false,
  dockOpen: false,
  // The first run tour has not been seen yet. Persisted with the rest of prefs
  // under the sv-cloud-demo localStorage key, so a refresh does not replay it
  // and "Reset the demo" does.
  tourDone: false,
  // The Alpha launch welcome, shown once before the tour. See Prefs.
  alphaWelcomeSeen: false,
  // Day, explicitly. See DEFAULT_THEME_CHOICE above for why the default is a
  // choice a person can see rather than a special case buried in resolution.
  themeChoice: DEFAULT_THEME_CHOICE,
};

export interface DemoStore extends DemoData {
  prefs: Prefs;
  setScenario: (id: ScenarioId) => void;
  setPref: <K extends keyof Prefs>(key: K, value: Prefs[K]) => void;
  startTimeline: (timeline: Omit<Timeline, 'speed'>) => void;
  clearTimelines: () => void;
  reset: () => void;
}

const DEFAULT_SCENARIO: ScenarioId = 'sam';

export const useDemoStore = create<DemoStore>()(
  persist(
    (set, get) => ({
      ...seed(DEFAULT_SCENARIO),
      prefs: DEFAULT_PREFS,

      setScenario: (id) => set({ ...seed(id), prefs: get().prefs }),

      setPref: (key, value) => set({ prefs: { ...get().prefs, [key]: value } }),

      startTimeline: (timeline) => {
        const prefs = get().prefs;
        const next: Timeline = { ...timeline, speed: prefs.speed };
        set({ timelines: [...get().timelines.filter((t) => t.id !== next.id), next] });
      },

      clearTimelines: () => set({ timelines: [] }),

      reset: () => set({ ...seed(DEFAULT_SCENARIO), prefs: DEFAULT_PREFS }),
    }),
    // Bumped when the seeded world grows a key that was empty before, which
    // M3 did: a persisted state from M2 carries an empty rows map and would
    // shadow the seeded one forever, so a reviewer with yesterday's tab open
    // would see every table report zero rows. A version change discards the
    // stale copy and reseeds, which is the correct answer here because none
    // of this state is a person's real work.
    {
      name: STORAGE_KEY,
      version: 4,
      migrate: () => ({ ...seed(DEFAULT_SCENARIO), prefs: DEFAULT_PREFS }),
    },
  ),
);

/** Read the store outside React, for the boot time attribute sync. */
export function readPrefs(): Prefs {
  return useDemoStore.getState().prefs;
}
